import { Link } from "react-router-dom";
import { createPortal } from "react-dom";
import { useEffect, useState } from "react";

/**
 * SignupPromptModal component props
 */
interface SignupPromptModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback to close the modal */
  onClose: () => void;
}

/**
 * Professional Signup Prompt Modal
 *
 * Renders via Portal at body level to overlay everything.
 * Perfectly centered in viewport.
 * Features:
 * - Portal-based rendering
 * - Body scroll lock when open
 * - Keyboard navigation (Escape key)
 * - Accessibility compliant (ARIA, keyboard navigation, 44px touch targets)
 * - Performance optimized (memoized callbacks, reduced motion support)
 */
const SignupPromptModal = ({ isOpen, onClose }: SignupPromptModalProps) => {
  // Detect current theme from document element
  const [isLightTheme, setIsLightTheme] = useState<boolean>(() => {
    if (typeof document === "undefined") return false;
    return document.documentElement.classList.contains("theme-light");
  });

  // Check for reduced motion preference
  const [prefersReducedMotion, setPrefersReducedMotion] =
    useState<boolean>(false);

  // Watch for theme changes
  useEffect(() => {
    if (typeof document === "undefined") return;

    const checkTheme = () => {
      setIsLightTheme(
        document.documentElement.classList.contains("theme-light"),
      );
    };

    checkTheme();

    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, [isOpen]);

  // Watch for reduced motion preference
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefersReducedMotion(mediaQuery.matches);

    const handleChange = (e: MediaQueryListEvent) => {
      setPrefersReducedMotion(e.matches);
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // Handle Escape key
  useEffect(() => {
    if (!isOpen) return undefined;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  // Theme-aware styling used in hover states
  void isLightTheme;

  if (!isOpen) {
    return null;
  }

  // Ensure we can render at body level
  if (typeof document === "undefined" || !document.body) {
    return null;
  }

  const modalContent = (
    <>
      {/* Backdrop - Click to close - More Prominent */}
      <div
        className="fixed inset-0 backdrop-blur-md"
        style={{
          zIndex: 99998,
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: isLightTheme
            ? "rgba(var(--bg-dark-rgb), 0.65)"
            : "rgba(var(--bg-dark-rgb), 0.75)",
          animation: prefersReducedMotion ? "none" : "fade-in 0.3s ease-out",
        }}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal Container - Perfectly Centered */}
      <div
        className="fixed inset-0 flex items-center justify-center px-4 sm:px-6 md:px-10"
        style={{
          zIndex: 99999,
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          overflow: "auto",
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="signup-modal-title"
        aria-describedby="signup-modal-description"
      >
        <div
          className="w-full max-w-md rounded-xl sm:rounded-2xl my-auto"
          style={{
            backgroundColor: isLightTheme
              ? "rgba(var(--text-main-rgb), 0.98)"
              : "rgba(var(--bg-dark-rgb), 0.98)",
            position: "relative",
            border: `2px solid ${isLightTheme ? "rgba(197, 157, 95, 0.3)" : "rgba(197, 157, 95, 0.4)"}`,
            boxShadow: isLightTheme
              ? "0 25px 60px -12px rgba(197, 157, 95, 0.4), 0 0 0 1px rgba(197, 157, 95, 0.2), 0 0 40px rgba(197, 157, 95, 0.15)"
              : "0 25px 60px -12px rgba(197, 157, 95, 0.5), 0 0 0 1px rgba(197, 157, 95, 0.3), 0 0 60px rgba(197, 157, 95, 0.2)",
            animation: prefersReducedMotion
              ? "none"
              : "modal-entrance 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Fixed Header with Close Button - Mobile-First Design */}
          <div
            className="sticky top-0 z-[100] flex items-center justify-between border-b border-[var(--border-default)] bg-[var(--bg-main)]/95 backdrop-blur-sm px-4 sm:px-6 md:px-10 py-3 sm:py-4 flex-shrink-0"
            style={{
              position: "sticky",
              top: 0,
              transform: "translateZ(0)",
              willChange: "transform",
            }}
          >
            <div className="flex items-center gap-3 sm:gap-4 md:gap-6 flex-1 min-w-0 pr-3">
              <div
                className="w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center flex-shrink-0"
                style={{
                  background:
                    "linear-gradient(135deg, rgba(197, 157, 95, 0.2) 0%, rgba(197, 157, 95, 0.1) 100%)",
                  boxShadow:
                    "0 0 20px rgba(197, 157, 95, 0.3), inset 0 0 20px rgba(197, 157, 95, 0.1)",
                  animation: prefersReducedMotion
                    ? "none"
                    : "pulse-glow 2s ease-in-out infinite",
                }}
                aria-hidden="true"
              >
                <svg
                  className="w-7 h-7 sm:w-8 sm:h-8"
                  style={{
                    color: "var(--accent)",
                    filter: "drop-shadow(0 2px 8px rgba(197, 157, 95, 0.4))",
                  }}
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <h2
                  id="signup-modal-title"
                  className="text-lg sm:text-xl md:text-2xl font-bold text-[var(--text-main)] truncate"
                  style={{
                    background: isLightTheme
                      ? "linear-gradient(135deg, var(--text-main) 0%, rgba(197, 157, 95, 0.9) 100%)"
                      : "linear-gradient(135deg, var(--text-main) 0%, rgba(197, 157, 95, 0.8) 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  Save Your Favorites
                </h2>
                <p
                  id="signup-modal-description"
                  className="text-sm sm:text-base text-[var(--accent)] font-medium mt-1 hidden sm:block"
                >
                  Unlock this feature with an account
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="flex-shrink-0 min-h-[44px] min-w-[44px] h-11 w-11 flex items-center justify-center rounded-full bg-[var(--bg-main)]/90 backdrop-blur-sm border border-[var(--border-default)] hover:bg-[var(--bg-hover)] shadow-md transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] focus-visible:ring-offset-2"
              style={{
                position: "relative",
                zIndex: 101,
                pointerEvents: "auto",
              }}
              aria-label="Close signup prompt modal"
            >
              <svg
                className="w-5 h-5 text-[var(--text-main)]"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2.5}
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Scrollable Content */}
          <div
            className="flex-1 overflow-y-auto overflow-x-hidden px-4 sm:px-6 md:px-10 py-4 sm:py-6"
            style={{
              WebkitOverflowScrolling: "touch",
              position: "relative",
              zIndex: 1,
            }}
          >
            {/* Mobile-only description */}
            <p className="text-base text-[var(--accent)] font-semibold mb-4 sm:hidden">
              Unlock this feature with an account
            </p>
            <div
              className="p-4 sm:p-5 rounded-xl mb-6"
              style={{
                background: isLightTheme
                  ? "linear-gradient(135deg, rgba(197, 157, 95, 0.08) 0%, rgba(197, 157, 95, 0.04) 100%)"
                  : "linear-gradient(135deg, rgba(197, 157, 95, 0.15) 0%, rgba(197, 157, 95, 0.08) 100%)",
                border: `1px solid ${isLightTheme ? "rgba(197, 157, 95, 0.15)" : "rgba(197, 157, 95, 0.2)"}`,
                boxShadow: "0 4px 12px rgba(197, 157, 95, 0.1)",
              }}
            >
              <p className="text-base sm:text-lg text-[var(--text-main)] leading-relaxed font-medium">
                Create a free account to{" "}
                <span className="font-bold" style={{ color: "var(--accent)" }}>
                  save your favorite dishes
                </span>{" "}
                and quickly reorder your go-to meals. It only takes a minute to
                get started!
              </p>
            </div>

            {/* Benefits */}
            <div className="space-y-4 mb-6" role="list">
              <div
                className="flex items-start gap-4 p-4 rounded-xl transition-all"
                role="listitem"
                style={{
                  background: isLightTheme
                    ? "rgba(var(--bg-dark-rgb), 0.02)"
                    : "rgba(var(--text-main-rgb), 0.03)",
                  border: `1px solid ${isLightTheme ? "rgba(197, 157, 95, 0.1)" : "rgba(197, 157, 95, 0.15)"}`,
                }}
              >
                <div
                  className="w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(197, 157, 95, 0.25) 0%, rgba(197, 157, 95, 0.15) 100%)",
                    boxShadow: "0 2px 8px rgba(197, 157, 95, 0.2)",
                  }}
                  aria-hidden="true"
                >
                  <svg
                    className="w-5 h-5 text-[var(--accent)]"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-base sm:text-lg font-bold text-[var(--text-main)]">
                    Save Favorite Dishes
                  </p>
                  <p className="text-sm sm:text-base text-[var(--text-muted)] mt-1">
                    Keep track of meals you love
                  </p>
                </div>
              </div>

              <div
                className="flex items-start gap-4 p-4 rounded-xl transition-all"
                role="listitem"
                style={{
                  background: isLightTheme
                    ? "rgba(var(--bg-dark-rgb), 0.02)"
                    : "rgba(var(--text-main-rgb), 0.03)",
                  border: `1px solid ${isLightTheme ? "rgba(197, 157, 95, 0.1)" : "rgba(197, 157, 95, 0.15)"}`,
                }}
              >
                <div
                  className="w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(197, 157, 95, 0.25) 0%, rgba(197, 157, 95, 0.15) 100%)",
                    boxShadow: "0 2px 8px rgba(197, 157, 95, 0.2)",
                  }}
                  aria-hidden="true"
                >
                  <svg
                    className="w-5 h-5 text-[var(--accent)]"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-base sm:text-lg font-bold text-[var(--text-main)]">
                    Order History
                  </p>
                  <p className="text-sm sm:text-base text-[var(--text-muted)] mt-1">
                    View all your past orders
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="px-4 sm:px-6 md:px-10 py-4 sm:py-5 md:py-6 pt-0 flex flex-col gap-3 sm:gap-4">
            <Link
              to="/signup"
              onClick={onClose}
              className="w-full px-6 py-4 min-h-[52px] rounded-xl sm:rounded-2xl font-bold text-base sm:text-lg text-center transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] focus-visible:ring-offset-2 group"
              style={{
                background:
                  "linear-gradient(135deg, rgba(197, 157, 95, 1) 0%, rgba(197, 157, 95, 0.85) 100%)",
                color: "#000",
                boxShadow:
                  "0 8px 24px rgba(197, 157, 95, 0.4), 0 4px 12px rgba(197, 157, 95, 0.3)",
                animation: prefersReducedMotion
                  ? "none"
                  : "button-pulse 2s ease-in-out infinite",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow =
                  "0 12px 32px rgba(197, 157, 95, 0.5), 0 6px 16px rgba(197, 157, 95, 0.4)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow =
                  "0 8px 24px rgba(197, 157, 95, 0.4), 0 4px 12px rgba(197, 157, 95, 0.3)";
              }}
              aria-label="Create a free account"
            >
              Create Free Account
            </Link>
            <Link
              to="/login"
              onClick={onClose}
              className="w-full px-6 py-3.5 min-h-[48px] rounded-xl sm:rounded-2xl font-semibold text-sm sm:text-base text-center transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] focus-visible:ring-offset-2"
              style={{
                border: `2px solid ${isLightTheme ? "rgba(197, 157, 95, 0.3)" : "rgba(197, 157, 95, 0.4)"}`,
                background: isLightTheme
                  ? "rgba(var(--bg-dark-rgb), 0.02)"
                  : "rgba(var(--text-main-rgb), 0.05)",
                color: "var(--text-main)",
              }}
              aria-label="Log in to existing account"
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = isLightTheme
                  ? "rgba(197, 157, 95, 0.08)"
                  : "rgba(197, 157, 95, 0.12)";
                e.currentTarget.style.borderColor = "var(--accent)";
                e.currentTarget.style.transform = "translateY(-1px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = isLightTheme
                  ? "rgba(var(--bg-dark-rgb), 0.02)"
                  : "rgba(var(--text-main-rgb), 0.05)";
                e.currentTarget.style.borderColor = isLightTheme
                  ? "rgba(197, 157, 95, 0.3)"
                  : "rgba(197, 157, 95, 0.4)";
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              I Already Have an Account
            </Link>
            <button
              onClick={onClose}
              className="w-full px-6 py-3 min-h-[44px] text-sm sm:text-base font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] focus-visible:ring-offset-2 rounded-lg"
              style={{
                color: "var(--text-muted)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = "var(--text-main)";
                e.currentTarget.style.backgroundColor = isLightTheme
                  ? "rgba(var(--bg-dark-rgb), 0.04)"
                  : "rgba(var(--text-main-rgb), 0.05)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = "var(--text-muted)";
                e.currentTarget.style.backgroundColor = "transparent";
              }}
              aria-label="Close modal and continue without signing up"
            >
              Maybe Later
            </button>
          </div>
        </div>
      </div>
    </>
  );

  // Render modal at document.body level using Portal
  return createPortal(modalContent, document.body);
};

export default SignupPromptModal;
